<template>
  <div class="careers-admin">
    <div class="ca-header">
      <a
        href="/home"
        class="ca-logo"
      >
        <img
          src="/logo.png"
          alt="TUT'TOP"
        >
      </a>
      <h1>{{ $t('careersAdmin.title') }}</h1>
      <a
        href="/carrieres"
        class="ca-view-btn"
      >{{ $t('careersAdmin.viewSite') }}</a>
    </div>

    <div class="ca-container">
      <div class="ca-toolbar">
        <h2>
          {{ offers.length }}
          {{ offers.length === 1 ? $t('careersAdmin.offerSingular') : $t('careersAdmin.offerPlural') }}
        </h2>
        <button
          class="ca-btn ca-btn-primary"
          @click="openModal(null)"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          ><line
            x1="12"
            y1="5"
            x2="12"
            y2="19"
          /><line
            x1="5"
            y1="12"
            x2="19"
            y2="12"
          /></svg>
          {{ $t('careersAdmin.newOffer') }}
        </button>
      </div>

      <div
        v-if="loading"
        class="ca-loading"
      >
        <div class="ca-spinner" />
      </div>

      <div
        v-else-if="offers.length === 0"
        class="ca-empty"
      >
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#38B6FF"
          stroke-width="1.5"
        ><rect
          x="2"
          y="7"
          width="20"
          height="14"
          rx="2"
        /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
        <p>{{ $t('careersAdmin.empty') }}</p>
        <button
          class="ca-btn ca-btn-primary"
          @click="openModal(null)"
        >
          {{ $t('careersAdmin.createFirst') }}
        </button>
      </div>

      <div
        v-else
        class="ca-table-wrap"
      >
        <table class="ca-table">
          <thead>
            <tr>
              <th>{{ $t('careersAdmin.table.title') }}</th>
              <th>{{ $t('careersAdmin.table.department') }}</th>
              <th>{{ $t('careersAdmin.table.type') }}</th>
              <th>{{ $t('careersAdmin.table.location') }}</th>
              <th>{{ $t('careersAdmin.table.duration') }}</th>
              <th>{{ $t('careersAdmin.table.status') }}</th>
              <th>{{ $t('careersAdmin.table.date') }}</th>
              <th>{{ $t('careersAdmin.table.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="o in offers"
              :key="o._id"
            >
              <td class="ca-title-cell">
                {{ o.title }}
              </td>
              <td>{{ o.department }}</td>
              <td><span
                class="ca-type-badge"
                :class="'ca-type-' + o.type"
              >{{ typeLabel(o.type) }}</span></td>
              <td>{{ o.location }}</td>
              <td>{{ o.duration }}</td>
              <td>
                <span
                  class="ca-status"
                  :class="o.active ? 'ca-active' : 'ca-inactive'"
                  @click="toggleActive(o)"
                >
                  {{ o.active ? $t('careersAdmin.status.active') : $t('careersAdmin.status.inactive') }}
                </span>
              </td>
              <td>{{ formatDate(o.createdAt) }}</td>
              <td class="ca-actions">
                <button
                  class="ca-icon-btn"
                  :title="$t('careersAdmin.actions.edit')"
                  @click="openModal(o)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  ><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                </button>
                <button
                  class="ca-icon-btn ca-icon-danger"
                  :title="$t('careersAdmin.actions.delete')"
                  @click="deleteOffer(o)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  ><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL -->
    <div
      v-if="showModal"
      class="ca-overlay"
      @click.self="showModal = false"
    >
      <div class="ca-modal">
        <div class="ca-modal-header">
          <h3>{{ editing ? $t('careersAdmin.modal.editTitle') : $t('careersAdmin.modal.newTitle') }}</h3>
          <button
            class="ca-close"
            @click="showModal = false"
          >
            &times;
          </button>
        </div>
        <div class="ca-modal-body">
          <div class="ca-form-row">
            <div class="ca-field">
              <label>{{ $t('careersAdmin.fields.title') }} *</label>
              <input
                v-model="form.title"
                :placeholder="$t('careersAdmin.placeholders.title')"
              >
            </div>
            <div class="ca-field">
              <label>{{ $t('careersAdmin.fields.department') }} *</label>
              <input
                v-model="form.department"
                :placeholder="$t('careersAdmin.placeholders.department')"
              >
            </div>
          </div>
          <div class="ca-form-row">
            <div class="ca-field">
              <label>{{ $t('careersAdmin.fields.type') }} *</label>
              <select v-model="form.type">
                <option value="pfe">
                  {{ $t('careers.types.pfe') }}
                </option>
                <option value="stage_2_mois">
                  {{ $t('careers.types.stage_2_mois') }}
                </option>
                <option value="stage_4_mois">
                  {{ $t('careers.types.stage_4_mois') }}
                </option>
                <option value="stage">
                  {{ $t('careersAdmin.typeStageOther') }}
                </option>
                <option value="cdd">
                  {{ $t('careers.types.cdd') }}
                </option>
                <option value="cdi">
                  {{ $t('careers.types.cdi') }}
                </option>
                <option value="alternance">
                  {{ $t('careers.types.alternance') }}
                </option>
                <option value="freelance">
                  {{ $t('careers.types.freelance') }}
                </option>
              </select>
            </div>
            <div class="ca-field">
              <label>{{ $t('careersAdmin.fields.location') }} *</label>
              <input
                v-model="form.location"
                :placeholder="$t('careersAdmin.placeholders.location')"
              >
            </div>
          </div>
          <div class="ca-form-row">
            <div class="ca-field">
              <label>{{ $t('careersAdmin.fields.duration') }} *</label>
              <input
                v-model="form.duration"
                :placeholder="$t('careersAdmin.placeholders.duration')"
              >
            </div>
            <div class="ca-field">
              <label>{{ $t('careersAdmin.fields.status') }}</label>
              <select v-model="form.active">
                <option :value="true">
                  {{ $t('careersAdmin.status.active') }}
                </option>
                <option :value="false">
                  {{ $t('careersAdmin.status.draft') }}
                </option>
              </select>
            </div>
          </div>
          <div class="ca-field ca-full">
            <label>{{ $t('careersAdmin.fields.description') }} *</label>
            <textarea
              v-model="form.description"
              rows="5"
              :placeholder="$t('careersAdmin.placeholders.description')"
            />
          </div>
          <div class="ca-field ca-full">
            <label>{{ $t('careersAdmin.fields.requirements') }}</label>
            <textarea
              v-model="form.requirements"
              rows="3"
              :placeholder="$t('careersAdmin.placeholders.requirements')"
            />
          </div>
          <div class="ca-field ca-full">
            <label>{{ $t('careersAdmin.fields.advantages') }}</label>
            <textarea
              v-model="form.advantages"
              rows="3"
              :placeholder="$t('careersAdmin.placeholders.advantages')"
            />
          </div>
        </div>
        <div class="ca-modal-footer">
          <button
            class="ca-btn ca-btn-ghost"
            @click="showModal = false"
          >
            {{ $t('careersAdmin.modal.cancel') }}
          </button>
          <button
            class="ca-btn ca-btn-primary"
            :disabled="saving"
            @click="saveOffer"
          >
            <span
              v-if="saving"
              class="ca-spinner-sm"
            />
            <span v-else>{{ editing ? $t('careersAdmin.modal.save') : $t('careersAdmin.modal.create') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'

export default {
  data() {
    return {
      offers: [],
      loading: true,
      showModal: false,
      editing: null,
      saving: false,
      form: this.emptyForm(),
    }
  },
  mounted() {
    this.fetchOffers()
  },
  methods: {
    emptyForm() {
      return {
        title: '', department: '', type: 'stage', location: '', duration: '', description: '', requirements: '', advantages: '', active: true,
      }
    },
    async fetchOffers() {
      this.loading = true
      try {
        const token = localStorage.getItem('accessToken') || ''
        const { data } = await axios.get('/internships/all', { headers: { token } })
        this.offers = data
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    openModal(offer) {
      if (offer) {
        this.editing = offer._id
        this.form = {
          title: offer.title, department: offer.department, type: offer.type, location: offer.location, duration: offer.duration, description: offer.description, requirements: offer.requirements || '', advantages: offer.advantages || '', active: offer.active,
        }
      } else {
        this.editing = null
        this.form = this.emptyForm()
      }
      this.showModal = true
    },
    async saveOffer() {
      if (!this.form.title || !this.form.department || !this.form.location || !this.form.duration || !this.form.description) return alert(this.$t('careersAdmin.validation.requiredFields'))
      this.saving = true
      const token = localStorage.getItem('accessToken') || ''
      try {
        if (this.editing) {
          await axios.put(`/internships/${this.editing}`, this.form, { headers: { token } })
        } else {
          await axios.post('/internships', this.form, { headers: { token } })
        }
        this.showModal = false
        this.fetchOffers()
      } catch (e) {
        alert(`${this.$t('careersAdmin.errors.generic')}: ${e.response ? e.response.data.error : e.message}`)
      } finally {
        this.saving = false
      }
    },
    async toggleActive(offer) {
      const token = localStorage.getItem('accessToken') || ''
      try {
        await axios.put(`/internships/${offer._id}`, { active: !offer.active }, { headers: { token } })
        offer.active = !offer.active
      } catch (e) {
        console.error(e)
      }
    },
    async deleteOffer(offer) {
      if (!confirm(this.$t('careersAdmin.confirmDelete', { title: offer.title }))) return
      const token = localStorage.getItem('accessToken') || ''
      try {
        await axios.delete(`/internships/${offer._id}`, { headers: { token } })
        this.offers = this.offers.filter(o => o._id !== offer._id)
      } catch (e) {
        console.error(e)
      }
    },
    typeLabel(t) {
      const key = `careers.types.${t}`
      const label = this.$t(key)
      return label === key ? t : label
    },
    formatDate(d) {
      if (!d) return ''
      const loc = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
      return new Date(d).toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })
    },
  },
}
</script>

<style scoped>
* { box-sizing: border-box; }
.careers-admin {
  min-height: 100vh;
  background: #f0f6ff;
  font-family: 'Montserrat', 'Inter', sans-serif;
}
.ca-header {
  background: linear-gradient(135deg, #38B6FF, #0884D0);
  padding: 20px 40px;
  display: flex;
  align-items: center;
  gap: 20px;
}
.ca-header img { height: 45px; }
.ca-header h1 { color: #fff; font-size: 1.3rem; font-weight: 700; margin: 0; flex: 1; }
.ca-view-btn {
  color: #fff;
  background: rgba(255,255,255,0.15);
  padding: 8px 18px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  transition: background 0.2s;
}
.ca-view-btn:hover { background: rgba(255,255,255,0.25); }
.ca-container { max-width: 1200px; margin: 0 auto; padding: 32px 24px; }
.ca-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.ca-toolbar h2 { font-size: 1.1rem; color: #1a1a2e; margin: 0; }
.ca-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 20px; border: none; border-radius: 10px;
  font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
}
.ca-btn-primary { background: linear-gradient(135deg, #38B6FF, #0884D0); color: #fff; }
.ca-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(56,182,255,0.35); }
.ca-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.ca-btn-ghost { background: transparent; color: #64748b; border: 1px solid #e0e7ef; }
.ca-btn-ghost:hover { background: #f8fafc; }
.ca-loading, .ca-empty { text-align: center; padding: 80px 20px; }
.ca-empty svg { margin-bottom: 16px; opacity: 0.5; }
.ca-empty p { color: #64748b; margin-bottom: 20px; }
.ca-spinner {
  width: 32px; height: 32px; border: 3px solid #e0e7ef; border-top-color: #38B6FF;
  border-radius: 50%; animation: spin 0.7s linear infinite; margin: 0 auto;
}
.ca-spinner-sm {
  width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  border-radius: 50%; animation: spin 0.7s linear infinite; display: inline-block;
}
@keyframes spin { to { transform: rotate(360deg); } }
.ca-table-wrap {
  background: #fff; border-radius: 14px; overflow: hidden;
  box-shadow: 0 2px 16px rgba(0,0,0,0.06);
}
.ca-table { width: 100%; border-collapse: collapse; }
.ca-table thead { background: #f8fafc; }
.ca-table th { padding: 14px 16px; font-size: 0.78rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; text-align: left; }
.ca-table td { padding: 14px 16px; font-size: 0.88rem; color: #1a1a2e; border-top: 1px solid #f0f2f5; }
.ca-table tbody tr:hover { background: #f8fafc; }
.ca-title-cell { font-weight: 600; max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ca-type-badge {
  padding: 4px 10px; border-radius: 20px; font-size: 0.75rem; font-weight: 600;
}
.ca-type-pfe { background: #fce4ec; color: #c62828; }
.ca-type-stage_2_mois { background: #e8f6ff; color: #0884D0; }
.ca-type-stage_4_mois { background: #e3f2fd; color: #1565c0; }
.ca-type-stage { background: #e8f6ff; color: #0884D0; }
.ca-type-cdd { background: #fff8e1; color: #f57f17; }
.ca-type-cdi { background: #e8fff0; color: #28c76f; }
.ca-type-alternance { background: #fff3e0; color: #e65100; }
.ca-type-freelance { background: #f3e8ff; color: #7c3aed; }
.ca-status {
  padding: 4px 10px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; cursor: pointer;
}
.ca-active { background: #e8fff0; color: #28c76f; }
.ca-inactive { background: #fef2f2; color: #ea5455; }
.ca-actions { display: flex; gap: 6px; }
.ca-icon-btn {
  width: 32px; height: 32px; border-radius: 8px; border: 1px solid #e0e7ef;
  background: #fff; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s; color: #64748b;
}
.ca-icon-btn:hover { background: #f0f6ff; color: #38B6FF; border-color: #38B6FF; }
.ca-icon-danger:hover { background: #fef2f2; color: #ea5455; border-color: #ea5455; }

/* Modal */
.ca-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 9999;
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.ca-modal {
  background: #fff; border-radius: 16px; width: 100%; max-width: 680px;
  max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.15);
}
.ca-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 28px; border-bottom: 1px solid #f0f2f5;
}
.ca-modal-header h3 { margin: 0; font-size: 1.15rem; color: #1a1a2e; }
.ca-close { background: none; border: none; font-size: 1.8rem; color: #9ca3af; cursor: pointer; line-height: 1; }
.ca-close:hover { color: #1a1a2e; }
.ca-modal-body { padding: 24px 28px; }
.ca-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.ca-field { display: flex; flex-direction: column; gap: 6px; }
.ca-field.ca-full { margin-bottom: 16px; }
.ca-field label { font-size: 0.82rem; font-weight: 600; color: #1a1a2e; }
.ca-field input, .ca-field select, .ca-field textarea {
  padding: 10px 14px; border: 1px solid #e0e7ef; border-radius: 8px;
  font-size: 0.9rem; color: #1a1a2e; outline: none; transition: border-color 0.2s;
  font-family: inherit; resize: vertical;
}
.ca-field input:focus, .ca-field select:focus, .ca-field textarea:focus {
  border-color: #38B6FF; box-shadow: 0 0 0 3px rgba(56,182,255,0.1);
}
.ca-modal-footer {
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 16px 28px; border-top: 1px solid #f0f2f5;
}
@media (max-width: 640px) {
  .ca-form-row { grid-template-columns: 1fr; }
  .ca-header { padding: 16px 20px; flex-wrap: wrap; }
  .ca-container { padding: 20px 16px; }
}
</style>
